import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Plus, Upload, Building2, Users, MapPin, Search, Trash2, Edit, 
  CheckCircle, XCircle, FileSpreadsheet, Download, Eye, Brain,
  MessageSquare, Tag, Globe, DollarSign, TrendingUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Services list for tradies
const SERVICES = [
  'electrician', 'plumber', 'painter', 'carpenter', 'concreter', 'roofer',
  'tiler', 'locksmith', 'cleaner', 'pest-control', 'pool-service', 'landscaper',
  'handyman', 'builder', 'hvac', 'asbestos-removal', 'tree-lopping'
];

// Australian states
const STATES = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'NT', 'ACT'];

// Hotspots categories
const HOTSPOTS_CATEGORIES = [
  'restaurants', 'cafes', 'bars', 'accommodation', 'attractions', 'shopping',
  'health', 'fitness', 'beauty', 'automotive', 'tradesman', 'professional',
  'education', 'entertainment', 'events', 'pets', 'travel', 'real-estate',
  'finance', 'technology', 'medical', 'legal', 'trades', 'home-services'
];

// Hotspots cities
const HOTSPOTS_CITIES = [
  'gold-coast', 'sydney', 'melbourne', 'brisbane', 'perth', 
  'adelaide', 'cairns', 'sunshine-coast', 'canberra', 'darwin'
];

// Knowledge base categories
const KB_CATEGORIES = ['general', 'location', 'business', 'faq', 'recommendation'];

export default function AdminPanel() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [activeTab, setActiveTab] = useState('tradies');
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Form state for new tradie
  const [tradieForm, setTradieForm] = useState({
    business_name: '',
    contact_name: '',
    email: '',
    phone: '',
    services: [],
    suburb: '',
    postcode: '',
    state: 'NSW',
    description: '',
    verified: false
  });

  // Form state for new hotspot business
  const [hotspotForm, setHotspotForm] = useState({
    name: '',
    description: '',
    category_id: 'restaurants',
    location_id: 'sydney',
    address: '',
    phone: '',
    email: '',
    website: '',
    price_range: '$$',
    rating: 4.5,
    image_url: '',
    verified: false,
    featured: false
  });

  // CSV upload state
  const [csvFile, setCsvFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState([]);

  // Kata Knowledge Base state
  const [kbEntries, setKbEntries] = useState([]);
  const [showKbForm, setShowKbForm] = useState(false);
  const [kbForm, setKbForm] = useState({
    question: '',
    answer: '',
    category: 'general',
    city: '',
    keywords: '',
    priority: 5
  });

  // My Boy Lollipop settings
  const [lollipopSettings, setLollipopSettings] = useState({
    dfans_url: 'https://dfans.co/myboylollipop'
  });

  const handleLogin = () => {
    if (password === 'lionheart2024') {
      setIsAuthenticated(true);
      toast.success('Welcome to Admin Panel');
      fetchListings();
    } else {
      toast.error('Invalid password');
    }
  };

  const fetchListings = async () => {
    setLoading(true);
    try {
      if (activeTab === 'kata-kb') {
        // Fetch Knowledge Base entries
        const res = await fetch(`${API_URL}/api/hotspots/knowledge-base?limit=100`);
        const data = await res.json();
        setKbEntries(data.entries || []);
        setListings([]);
      } else if (activeTab === 'lollipop') {
        // Fetch Lollipop settings
        const res = await fetch(`${API_URL}/api/lollipop/settings`);
        const data = await res.json();
        setLollipopSettings(data);
        setListings([]);
      } else {
        const endpoint = activeTab === 'tradies' 
          ? `${API_URL}/api/tradies?limit=100`
          : `${API_URL}/api/hotspots/businesses?limit=100`;
        
        const res = await fetch(endpoint);
        const data = await res.json();
        setListings(activeTab === 'tradies' ? data : data.businesses || []);
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchListings();
    }
  }, [activeTab, isAuthenticated]);

  // Add Knowledge Base Entry
  const handleAddKbEntry = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const keywordsArray = kbForm.keywords.split(',').map(k => k.trim()).filter(Boolean);
      const payload = {
        question: kbForm.question,
        answer: kbForm.answer,
        category: kbForm.category,
        city: kbForm.city || null,
        keywords: keywordsArray,
        priority: parseInt(kbForm.priority) || 5
      };
      
      const res = await fetch(`${API_URL}/api/hotspots/knowledge-base`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        toast.success('Knowledge entry added!');
        setShowKbForm(false);
        setKbForm({ question: '', answer: '', category: 'general', city: '', keywords: '', priority: 5 });
        fetchListings();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to add entry');
      }
    } catch (error) {
      toast.error('Failed to add knowledge entry');
    } finally {
      setLoading(false);
    }
  };

  // Delete Knowledge Base Entry
  const handleDeleteKbEntry = async (entryId) => {
    if (!window.confirm('Delete this knowledge entry?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/hotspots/knowledge-base/${entryId}`, {
        method: 'DELETE'
      });
      
      if (res.ok) {
        toast.success('Entry deleted');
        fetchListings();
      } else {
        toast.error('Failed to delete entry');
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleAddTradie = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/tradies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(tradieForm)
      });
      
      if (res.ok) {
        toast.success('Tradie added successfully!');
        setShowAddForm(false);
        setTradieForm({
          business_name: '', contact_name: '', email: '', phone: '',
          services: [], suburb: '', postcode: '', state: 'NSW', description: '', verified: false
        });
        fetchListings();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to add tradie');
      }
    } catch (error) {
      toast.error('Failed to add tradie');
    } finally {
      setLoading(false);
    }
  };

  const handleAddHotspot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/hotspots/businesses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(hotspotForm)
      });
      
      if (res.ok) {
        toast.success('Business added successfully!');
        setShowAddForm(false);
        setHotspotForm({
          name: '', description: '', category_id: 'restaurants', location_id: 'sydney',
          address: '', phone: '', email: '', website: '', price_range: '$$',
          rating: 4.5, image_url: '', verified: false, featured: false
        });
        fetchListings();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to add business');
      }
    } catch (error) {
      toast.error('Failed to add business');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this listing?')) return;
    
    try {
      const endpoint = activeTab === 'tradies'
        ? `${API_URL}/api/tradies/${id}/simple-delete`
        : `${API_URL}/api/hotspots/businesses/${id}`;
      
      const res = await fetch(endpoint, {
        method: activeTab === 'tradies' ? 'POST' : 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: activeTab === 'tradies' ? JSON.stringify({ password: 'tradie2024' }) : undefined
      });
      
      if (res.ok) {
        toast.success('Deleted successfully');
        fetchListings();
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleCSVUpload = async () => {
    if (!csvFile) return;
    
    setLoading(true);
    const reader = new FileReader();
    
    reader.onload = async (e) => {
      try {
        const text = e.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
        
        let successCount = 0;
        let errorCount = 0;
        
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map(v => v.trim());
          const record = {};
          
          headers.forEach((header, idx) => {
            record[header] = values[idx] || '';
          });
          
          try {
            if (activeTab === 'tradies') {
              // Map CSV columns to tradie fields
              const tradie = {
                business_name: record.business_name || record.name || '',
                contact_name: record.contact_name || record.contact || '',
                email: record.email || `${(record.business_name || 'business').toLowerCase().replace(/\s+/g, '')}@example.com`,
                phone: record.phone || '0400 000 000',
                services: (record.services || record.service || 'handyman').split(';').map(s => s.trim()),
                suburb: record.suburb || record.city || '',
                postcode: record.postcode || '2000',
                state: record.state || 'NSW',
                description: record.description || `Professional ${record.services || 'trade'} services in ${record.suburb || 'your area'}.`,
                verified: record.verified === 'true' || false
              };
              
              const res = await fetch(`${API_URL}/api/tradies`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(tradie)
              });
              
              if (res.ok) successCount++;
              else errorCount++;
            } else {
              // Map CSV columns to hotspot fields
              const business = {
                name: record.name || record.business_name || '',
                description: record.description || '',
                category_id: record.category || record.category_id || 'restaurants',
                location_id: record.city || record.location_id || 'sydney',
                address: record.address || '',
                phone: record.phone || '',
                email: record.email || '',
                website: record.website || '',
                price_range: record.price_range || '$$',
                rating: parseFloat(record.rating) || 4.5,
                image_url: record.image_url || record.image || '',
                verified: record.verified === 'true' || false,
                featured: record.featured === 'true' || false
              };
              
              const res = await fetch(`${API_URL}/api/hotspots/businesses`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(business)
              });
              
              if (res.ok) successCount++;
              else errorCount++;
            }
          } catch (err) {
            errorCount++;
          }
        }
        
        toast.success(`Imported ${successCount} records. ${errorCount} errors.`);
        setShowBulkUpload(false);
        setCsvFile(null);
        setCsvPreview([]);
        fetchListings();
      } catch (error) {
        toast.error('Failed to parse CSV');
      } finally {
        setLoading(false);
      }
    };
    
    reader.readAsText(csvFile);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCsvFile(file);
      
      // Preview first 5 rows
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target.result;
        const lines = text.split('\n').slice(0, 6);
        setCsvPreview(lines);
      };
      reader.readAsText(file);
    }
  };

  const downloadTemplate = () => {
    const headers = activeTab === 'tradies'
      ? 'business_name,contact_name,email,phone,services,suburb,postcode,state,description,verified'
      : 'name,description,category,city,address,phone,email,website,price_range,rating,image_url,verified,featured';
    
    const example = activeTab === 'tradies'
      ? '\nAce Electrical,John Smith,john@aceelectrical.com.au,0412345678,electrician,Sydney,2000,NSW,Professional electrical services,false'
      : '\nBest Cafe,Great coffee and food,cafes,sydney,123 Main St,0298765432,info@bestcafe.com.au,https://bestcafe.com.au,$$,4.5,https://example.com/image.jpg,false,false';
    
    const blob = new Blob([headers + example], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${activeTab}_template.csv`;
    a.click();
  };

  const filteredListings = listings.filter(item => {
    const name = activeTab === 'tradies' ? item.business_name : item.name;
    return name?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Helmet>
          <title>Admin Panel | Lionheart Digital</title>
        </Helmet>
        <div className="bg-slate-800 rounded-2xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-white mb-2">Admin Panel</h1>
            <p className="text-slate-400">Lionheart Digital Empire</p>
          </div>
          <div className="space-y-4">
            <Input
              type="password"
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
              className="bg-slate-700 border-slate-600 text-white"
            />
            <Button onClick={handleLogin} className="w-full bg-orange-500 hover:bg-orange-600">
              Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      <Helmet>
        <title>Admin Panel | Lionheart Digital</title>
      </Helmet>
      
      {/* Header */}
      <header className="bg-slate-900 text-white p-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <h1 className="text-xl font-bold">Lionheart Digital Admin</h1>
          <div className="flex items-center gap-4">
            <span className="text-slate-400 text-sm">
              {activeTab === 'kata-kb' ? `${kbEntries.length} KB entries` : `${listings.length} listings`}
            </span>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setIsAuthenticated(false)}
              className="border-slate-600 text-slate-300 hover:bg-slate-800"
            >
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={activeTab === 'tradies' ? 'default' : 'outline'}
            onClick={() => setActiveTab('tradies')}
            className={activeTab === 'tradies' ? 'bg-orange-500 hover:bg-orange-600' : ''}
          >
            <Users className="w-4 h-4 mr-2" />
            Tradies
          </Button>
          <Button
            variant={activeTab === 'hotspots' ? 'default' : 'outline'}
            onClick={() => setActiveTab('hotspots')}
            className={activeTab === 'hotspots' ? 'bg-sky-500 hover:bg-sky-600' : ''}
          >
            <Building2 className="w-4 h-4 mr-2" />
            Hotspots
          </Button>
          <Button
            variant={activeTab === 'kata-kb' ? 'default' : 'outline'}
            onClick={() => setActiveTab('kata-kb')}
            className={activeTab === 'kata-kb' ? 'bg-purple-500 hover:bg-purple-600' : ''}
            data-testid="kata-kb-tab"
          >
            <Brain className="w-4 h-4 mr-2" />
            Kata KB
          </Button>
          <Button
            variant={activeTab === 'lollipop' ? 'default' : 'outline'}
            onClick={() => setActiveTab('lollipop')}
            className={activeTab === 'lollipop' ? 'bg-pink-500 hover:bg-pink-600' : ''}
            data-testid="lollipop-tab"
          >
            <Globe className="w-4 h-4 mr-2" />
            Lollipop
          </Button>
        </div>

        {/* Actions Bar - Tradies & Hotspots */}
        {(activeTab === 'tradies' || activeTab === 'hotspots') && (
          <div className="bg-white rounded-xl p-4 mb-6 flex flex-wrap gap-4 items-center justify-between">
            <div className="flex gap-2">
              <Button onClick={() => setShowAddForm(true)} className="bg-green-600 hover:bg-green-700">
                <Plus className="w-4 h-4 mr-2" />
                Add {activeTab === 'tradies' ? 'Tradie' : 'Business'}
              </Button>
              <Button onClick={() => setShowBulkUpload(true)} variant="outline">
                <Upload className="w-4 h-4 mr-2" />
                Bulk Upload CSV
              </Button>
              <Button onClick={downloadTemplate} variant="outline">
                <Download className="w-4 h-4 mr-2" />
                Download Template
              </Button>
            </div>
            
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search listings..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-64"
              />
            </div>
          </div>
        )}

        {/* Actions Bar - Kata KB */}
        {activeTab === 'kata-kb' && (
          <div className="bg-white rounded-xl p-4 mb-6 flex flex-wrap gap-4 items-center justify-between">
            <div className="flex gap-2">
              <Button onClick={() => setShowKbForm(true)} className="bg-purple-600 hover:bg-purple-700" data-testid="add-kb-entry-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Q&A Entry
              </Button>
            </div>
            <div className="text-sm text-slate-500">
              <Brain className="w-4 h-4 inline mr-1" />
              {kbEntries.length} knowledge entries - Kata will check these first before using AI
            </div>
          </div>
        )}

        {/* Listings Table - Tradies & Hotspots */}
        {(activeTab === 'tradies' || activeTab === 'hotspots') && (
          <div className="bg-white rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left p-4 font-medium text-slate-600">Name</th>
                <th className="text-left p-4 font-medium text-slate-600">
                  {activeTab === 'tradies' ? 'Service' : 'Category'}
                </th>
                <th className="text-left p-4 font-medium text-slate-600">Location</th>
                <th className="text-left p-4 font-medium text-slate-600">Status</th>
                <th className="text-right p-4 font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="p-8 text-center text-slate-500">Loading...</td>
                </tr>
              ) : filteredListings.length === 0 ? (
                <tr>
                  <td colSpan={5} className="p-8 text-center text-slate-500">No listings found</td>
                </tr>
              ) : (
                filteredListings.map((item) => (
                  <tr key={item.id} className="border-b hover:bg-slate-50">
                    <td className="p-4">
                      <div className="font-medium text-slate-900">
                        {activeTab === 'tradies' ? item.business_name : item.name}
                      </div>
                      <div className="text-sm text-slate-500">
                        {activeTab === 'tradies' ? item.contact_name : item.address}
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="px-2 py-1 bg-slate-100 rounded text-sm">
                        {activeTab === 'tradies' ? item.services?.[0] : item.category_id}
                      </span>
                    </td>
                    <td className="p-4 text-slate-600">
                      {activeTab === 'tradies' ? `${item.suburb}, ${item.state}` : item.location_id}
                    </td>
                    <td className="p-4">
                      {item.verified ? (
                        <span className="flex items-center text-green-600 text-sm">
                          <CheckCircle className="w-4 h-4 mr-1" /> Verified
                        </span>
                      ) : (
                        <span className="flex items-center text-amber-600 text-sm">
                          <XCircle className="w-4 h-4 mr-1" /> Unverified
                        </span>
                      )}
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            const url = activeTab === 'tradies'
                              ? `/tradie/${item.id}`
                              : `/hotspots/business/${item.id}`;
                            window.open(url, '_blank');
                          }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDelete(item.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        )}

        {/* Kata Knowledge Base Table */}
        {activeTab === 'kata-kb' && (
          <div className="bg-white rounded-xl overflow-hidden" data-testid="kb-entries-list">
            <div className="p-4 bg-purple-50 border-b">
              <h3 className="font-semibold text-purple-900">Kata's Knowledge Base</h3>
              <p className="text-sm text-purple-700">Add custom Q&A entries that Kata will use to answer questions. She checks this database FIRST before using AI.</p>
            </div>
            {loading ? (
              <div className="p-8 text-center text-slate-500">Loading...</div>
            ) : kbEntries.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <Brain className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                <p>No knowledge entries yet</p>
                <p className="text-sm">Add Q&A entries to train Kata with custom responses</p>
              </div>
            ) : (
              <div className="divide-y">
                {kbEntries.map((entry) => (
                  <div key={entry.id} className="p-4 hover:bg-slate-50">
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                            {entry.category}
                          </span>
                          {entry.city && (
                            <span className="px-2 py-0.5 bg-sky-100 text-sky-700 rounded text-xs">
                              {entry.city}
                            </span>
                          )}
                          <span className="text-xs text-slate-400">Priority: {entry.priority}</span>
                        </div>
                        <p className="font-medium text-slate-900 mb-1">Q: {entry.question}</p>
                        <p className="text-slate-600 text-sm">A: {entry.answer}</p>
                        {entry.keywords?.length > 0 && (
                          <div className="flex gap-1 mt-2 flex-wrap">
                            {entry.keywords.map((kw, i) => (
                              <span key={i} className="px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded text-xs">
                                {kw}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDeleteKbEntry(entry.id)}
                        data-testid={`delete-kb-${entry.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* My Boy Lollipop Settings */}
        {activeTab === 'lollipop' && (
          <div className="bg-white rounded-xl overflow-hidden" data-testid="lollipop-settings">
            <div className="p-4 bg-gradient-to-r from-pink-500 to-orange-500 text-white">
              <h3 className="font-bold text-lg">My Boy Lollipop Settings</h3>
              <p className="text-sm text-white/80">Manage your dFans profile link and landing page settings</p>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  dFans Profile URL
                </label>
                <div className="flex gap-2">
                  <Input
                    type="url"
                    placeholder="https://dfans.co/yourprofile"
                    value={lollipopSettings.dfans_url || ''}
                    onChange={(e) => setLollipopSettings({...lollipopSettings, dfans_url: e.target.value})}
                    className="flex-1"
                    data-testid="dfans-url-input"
                  />
                  <Button
                    onClick={async () => {
                      try {
                        const res = await fetch(`${API_URL}/api/lollipop/settings?dfans_url=${encodeURIComponent(lollipopSettings.dfans_url)}`, {
                          method: 'POST',
                          headers: {
                            'X-Admin-Password': 'lionheart2024'
                          }
                        });
                        if (res.ok) {
                          toast.success('dFans URL updated!');
                        } else {
                          toast.error('Failed to update');
                        }
                      } catch (err) {
                        toast.error('Error saving settings');
                      }
                    }}
                    className="bg-pink-500 hover:bg-pink-600"
                    data-testid="save-dfans-url"
                  >
                    Save
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  All "Subscribe" buttons on My Boy Lollipop will link to this URL
                </p>
              </div>
              
              <div className="border-t pt-4">
                <h4 className="font-medium text-slate-900 mb-2">Quick Links</h4>
                <div className="flex flex-wrap gap-2">
                  <a 
                    href="/myboylollipop" 
                    target="_blank"
                    className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded text-sm text-slate-700"
                  >
                    View Landing Page →
                  </a>
                  <a 
                    href={lollipopSettings.dfans_url} 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-1.5 bg-pink-100 hover:bg-pink-200 rounded text-sm text-pink-700"
                  >
                    Open dFans Profile →
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add Form Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              Add New {activeTab === 'tradies' ? 'Tradie' : 'Business'}
            </h2>
            
            {activeTab === 'tradies' ? (
              <form onSubmit={handleAddTradie} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Business Name *</label>
                    <Input
                      required
                      value={tradieForm.business_name}
                      onChange={(e) => setTradieForm({...tradieForm, business_name: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Contact Name *</label>
                    <Input
                      required
                      value={tradieForm.contact_name}
                      onChange={(e) => setTradieForm({...tradieForm, contact_name: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Email *</label>
                    <Input
                      type="email"
                      required
                      value={tradieForm.email}
                      onChange={(e) => setTradieForm({...tradieForm, email: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Phone *</label>
                    <Input
                      required
                      value={tradieForm.phone}
                      onChange={(e) => setTradieForm({...tradieForm, phone: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Services *</label>
                    <select
                      multiple
                      className="w-full border rounded-lg p-2 h-24"
                      value={tradieForm.services}
                      onChange={(e) => setTradieForm({
                        ...tradieForm, 
                        services: Array.from(e.target.selectedOptions, opt => opt.value)
                      })}
                    >
                      {SERVICES.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">State *</label>
                    <select
                      className="w-full border rounded-lg p-2"
                      value={tradieForm.state}
                      onChange={(e) => setTradieForm({...tradieForm, state: e.target.value})}
                    >
                      {STATES.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Suburb *</label>
                    <Input
                      required
                      value={tradieForm.suburb}
                      onChange={(e) => setTradieForm({...tradieForm, suburb: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Postcode *</label>
                    <Input
                      required
                      value={tradieForm.postcode}
                      onChange={(e) => setTradieForm({...tradieForm, postcode: e.target.value})}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <textarea
                    className="w-full border rounded-lg p-2 h-24"
                    value={tradieForm.description}
                    onChange={(e) => setTradieForm({...tradieForm, description: e.target.value})}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="verified"
                    checked={tradieForm.verified}
                    onChange={(e) => setTradieForm({...tradieForm, verified: e.target.checked})}
                  />
                  <label htmlFor="verified">Mark as Verified</label>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowAddForm(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={loading} className="bg-orange-500 hover:bg-orange-600">
                    {loading ? 'Adding...' : 'Add Tradie'}
                  </Button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleAddHotspot} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Business Name *</label>
                    <Input
                      required
                      value={hotspotForm.name}
                      onChange={(e) => setHotspotForm({...hotspotForm, name: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Category *</label>
                    <select
                      className="w-full border rounded-lg p-2"
                      value={hotspotForm.category_id}
                      onChange={(e) => setHotspotForm({...hotspotForm, category_id: e.target.value})}
                    >
                      {HOTSPOTS_CATEGORIES.map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">City *</label>
                    <select
                      className="w-full border rounded-lg p-2"
                      value={hotspotForm.location_id}
                      onChange={(e) => setHotspotForm({...hotspotForm, location_id: e.target.value})}
                    >
                      {HOTSPOTS_CITIES.map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Address</label>
                    <Input
                      value={hotspotForm.address}
                      onChange={(e) => setHotspotForm({...hotspotForm, address: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Phone</label>
                    <Input
                      value={hotspotForm.phone}
                      onChange={(e) => setHotspotForm({...hotspotForm, phone: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Email</label>
                    <Input
                      type="email"
                      value={hotspotForm.email}
                      onChange={(e) => setHotspotForm({...hotspotForm, email: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Website</label>
                    <Input
                      value={hotspotForm.website}
                      onChange={(e) => setHotspotForm({...hotspotForm, website: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Price Range</label>
                    <select
                      className="w-full border rounded-lg p-2"
                      value={hotspotForm.price_range}
                      onChange={(e) => setHotspotForm({...hotspotForm, price_range: e.target.value})}
                    >
                      <option value="$">$ (Budget)</option>
                      <option value="$$">$$ (Moderate)</option>
                      <option value="$$$">$$$ (Upscale)</option>
                      <option value="$$$$">$$$$ (Luxury)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Rating (1-5)</label>
                    <Input
                      type="number"
                      min="1"
                      max="5"
                      step="0.1"
                      value={hotspotForm.rating}
                      onChange={(e) => setHotspotForm({...hotspotForm, rating: parseFloat(e.target.value)})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Image URL</label>
                    <Input
                      value={hotspotForm.image_url}
                      onChange={(e) => setHotspotForm({...hotspotForm, image_url: e.target.value})}
                      placeholder="https://..."
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <textarea
                    className="w-full border rounded-lg p-2 h-24"
                    value={hotspotForm.description}
                    onChange={(e) => setHotspotForm({...hotspotForm, description: e.target.value})}
                  />
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="hotspot-verified"
                      checked={hotspotForm.verified}
                      onChange={(e) => setHotspotForm({...hotspotForm, verified: e.target.checked})}
                    />
                    <label htmlFor="hotspot-verified">Verified</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="hotspot-featured"
                      checked={hotspotForm.featured}
                      onChange={(e) => setHotspotForm({...hotspotForm, featured: e.target.checked})}
                    />
                    <label htmlFor="hotspot-featured">Featured</label>
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowAddForm(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={loading} className="bg-sky-500 hover:bg-sky-600">
                    {loading ? 'Adding...' : 'Add Business'}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {showBulkUpload && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl">
            <h2 className="text-xl font-bold mb-4">Bulk Upload CSV</h2>
            
            <div className="space-y-4">
              <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center">
                <FileSpreadsheet className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="csv-upload"
                />
                <label
                  htmlFor="csv-upload"
                  className="cursor-pointer text-sky-600 hover:text-sky-700 font-medium"
                >
                  Click to select CSV file
                </label>
                {csvFile && (
                  <p className="mt-2 text-sm text-slate-600">{csvFile.name}</p>
                )}
              </div>

              {csvPreview.length > 0 && (
                <div className="bg-slate-50 rounded-lg p-4 overflow-x-auto">
                  <p className="text-sm font-medium text-slate-600 mb-2">Preview:</p>
                  <pre className="text-xs text-slate-500">
                    {csvPreview.join('\n')}
                  </pre>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => {
                  setShowBulkUpload(false);
                  setCsvFile(null);
                  setCsvPreview([]);
                }}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleCSVUpload} 
                  disabled={!csvFile || loading}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {loading ? 'Uploading...' : 'Upload & Import'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Knowledge Base Entry Modal */}
      {showKbForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" data-testid="kb-form-modal">
          <div className="bg-white rounded-2xl p-6 w-full max-w-xl">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Brain className="w-5 h-5 text-purple-600" />
              Add Knowledge Entry
            </h2>
            <p className="text-sm text-slate-500 mb-4">
              Kata will use this Q&A to answer matching questions before falling back to AI.
            </p>
            
            <form onSubmit={handleAddKbEntry} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Question / Trigger *</label>
                <Input
                  required
                  placeholder="e.g., What are the best restaurants in Brisbane?"
                  value={kbForm.question}
                  onChange={(e) => setKbForm({...kbForm, question: e.target.value})}
                  data-testid="kb-question-input"
                />
                <p className="text-xs text-slate-400 mt-1">The question or topic Kata should recognize</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Answer *</label>
                <textarea
                  required
                  rows={4}
                  className="w-full border rounded-lg p-2 text-sm"
                  placeholder="e.g., For the best restaurants in Brisbane, I recommend checking out Stokehouse Q for waterfront dining..."
                  value={kbForm.answer}
                  onChange={(e) => setKbForm({...kbForm, answer: e.target.value})}
                  data-testid="kb-answer-input"
                />
                <p className="text-xs text-slate-400 mt-1">Kata's response when this topic is matched</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Category</label>
                  <select
                    className="w-full border rounded-lg p-2 text-sm"
                    value={kbForm.category}
                    onChange={(e) => setKbForm({...kbForm, category: e.target.value})}
                    data-testid="kb-category-select"
                  >
                    {KB_CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">City (optional)</label>
                  <select
                    className="w-full border rounded-lg p-2 text-sm"
                    value={kbForm.city}
                    onChange={(e) => setKbForm({...kbForm, city: e.target.value})}
                    data-testid="kb-city-select"
                  >
                    <option value="">All cities</option>
                    {HOTSPOTS_CITIES.map(city => (
                      <option key={city} value={city}>{city.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Keywords (comma separated)</label>
                  <Input
                    placeholder="e.g., restaurant, dining, food, brisbane"
                    value={kbForm.keywords}
                    onChange={(e) => setKbForm({...kbForm, keywords: e.target.value})}
                    data-testid="kb-keywords-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Priority (1-10)</label>
                  <Input
                    type="number"
                    min="1"
                    max="10"
                    value={kbForm.priority}
                    onChange={(e) => setKbForm({...kbForm, priority: e.target.value})}
                    data-testid="kb-priority-input"
                  />
                  <p className="text-xs text-slate-400 mt-1">Higher = checked first</p>
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setShowKbForm(false)}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={loading} 
                  className="bg-purple-600 hover:bg-purple-700"
                  data-testid="kb-submit-btn"
                >
                  {loading ? 'Adding...' : 'Add Entry'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
